import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Save, X } from 'lucide-react';

const TeamForm = ({ onSubmit, initialData = null, isSaving = false }) => {
  const [teamName, setTeamName] = useState('');
  const [sport, setSport] = useState('');
  const [roster, setRoster] = useState([{ playerName: '', playerNumber: '' }]);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) {
      setTeamName(initialData.teamName || '');
      setSport(initialData.sport || '');
      setRoster(initialData.roster.length > 0 ? initialData.roster : [{ playerName: '', playerNumber: '' }]);
    }
  }, [initialData]);

  const handleRosterChange = (index, event) => {
    const values = [...roster];
    values[index][event.target.name] = event.target.value;
    setRoster(values);
  };

  const handleAddPlayer = () => {
    setRoster([...roster, { playerName: '', playerNumber: '' }]);
  };

  const handleRemovePlayer = (index) => {
    const values = [...roster];
    values.splice(index, 1);
    setRoster(values);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const finalRoster = roster.filter(p => p.playerName.trim() !== '');
    onSubmit({ teamName, sport, roster: finalRoster });
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl text-sm text-white placeholder-white/60 outline-none transition-colors";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* Team Details card */}
      <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-royal-blue to-electric-green" />
        <div className="p-6">
          <h2 className="font-black text-gray-200 text-base uppercase tracking-wide mb-5">Team Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="teamName" className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Team Name</label>
              <input
                type="text"
                id="teamName"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g. Thunder Hawks"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="sport" className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Sport</label>
              <select
                id="sport"
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                className={inputClass}
                style={{ colorScheme: 'dark', background: '#0f172a' }}
                required
              >
                <option value="">Select a sport…</option>
                <option value="Baseball">⚾ Baseball</option>
                <option value="Softball">🥎 Softball</option>
                <option value="Soccer">⚽ Soccer</option>
                <option value="Football">🏈 Football</option>
                <option value="Basketball">🏀 Basketball</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Roster card */}
      <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-yellow-400 to-electric-green" />
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-gray-200 text-base uppercase tracking-wide">Roster</h2>
            <span className="text-xs font-bold text-gray-400 bg-white/10 rounded-full px-3 py-1">{roster.length} players</span>
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-12 gap-3 mb-2 px-1">
            <span className="col-span-1 text-xs font-bold text-gray-500 uppercase tracking-wide">#</span>
            <span className="col-span-8 text-xs font-bold text-gray-500 uppercase tracking-wide">Player Name</span>
            <span className="col-span-2 text-xs font-bold text-gray-500 uppercase tracking-wide">Jersey</span>
          </div>

          <div className="space-y-2">
            {roster.map((player, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-center">
                {/* Row number */}
                <div className="col-span-1 flex items-center justify-center w-7 h-7 rounded-lg bg-white/10 text-xs font-black text-gray-400">
                  {index + 1}
                </div>
                {/* Name */}
                <input
                  type="text"
                  name="playerName"
                  placeholder="Player name"
                  value={player.playerName}
                  onChange={e => handleRosterChange(index, e)}
                  className={`col-span-8 ${inputClass}`}
                />
                {/* Number */}
                <input
                  type="text"
                  name="playerNumber"
                  placeholder="#"
                  value={player.playerNumber}
                  onChange={e => handleRosterChange(index, e)}
                  className={`col-span-2 ${inputClass} text-center`}
                />
                {/* Remove */}
                <div className="col-span-1 flex justify-center">
                  <button
                    type="button"
                    onClick={() => handleRemovePlayer(index)}
                    disabled={roster.length === 1}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:text-red-400 hover:bg-red-500/20 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddPlayer}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="h-4 w-4" />Add Player
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/teams')}
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white font-bold py-2.5 px-5 rounded-xl border-2 border-white/20 transition-colors text-sm"
        >
          <X className="h-4 w-4" />Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-black py-2.5 px-6 rounded-xl transition-colors shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving…' : 'Save Team'}
        </button>
      </div>
    </form>
  );
};

export default TeamForm;
